/**
 * CampaignBuildAnimation.jsx
 * ---------------------------------------------------------------------------
 * Onboarding "AI builds your campaign" animation for Houdiny.
 * Self-contained, zero dependencies (pure React). This is ONLY the animation —
 * no outer frame, no page chrome. It fills whatever container you put it in.
 *
 *   import CampaignBuildAnimation from './CampaignBuildAnimation';
 *
 *   <div style={{ width: '100%', height: 560 }}>
 *     <CampaignBuildAnimation />
 *   </div>
 *
 * Props (all optional):
 *   speed        number  1 = ~5s total. 1.5 ≈ 3.3s, 0.7 ≈ 7s.            (default 0.7)
 *   ending       string  'reveal' (pan + zoom-out) | 'stay'             (default 'stay')
 *   autoPlay     bool    play once on mount                             (default true)
 *   loop         bool    replay forever (off = play once, recommended)  (default false)
 *   showStatus   bool    floating "AI is building…" pill                (default true)
 *   showGrid     bool    dotted builder background                      (default true)
 *   successTitle string  end-notification headline                     (default "Congratulations!")
 *   successText  string  end-notification subtext
 *   tokens       object  brand overrides, e.g. { brand:'#1A56FF' }      (see DEFAULT_TOKENS)
 *   onComplete   func    called when the animation finishes
 *   showControls bool    demo Replay/Speed bar (dev only)               (default false)
 *   className / style     forwarded to the animation container
 *
 * Sizing: give the parent (or pass `style`) a width + height. The animation
 * scales to fit responsively. All class names are `hdy-`prefixed and keyframes
 * `hdy-*` so nothing collides with your app's CSS.
 * TypeScript: rename to .tsx and add a props interface — no other changes needed.
 * ---------------------------------------------------------------------------
 */
import { useEffect, useRef } from 'react';

/* ============================ BRAND TOKENS ============================ */
const DEFAULT_TOKENS = {
  brand: '#3366FF', brandStrong: '#1E45E6', brandSoft: '#EAF0FF',
  violet: '#7C5CFF', pink: '#EC4899', red: '#F0506E', amber: '#FF9F43',
  teal: '#2BB5C9', blue2: '#3B82F6', green: '#16A34A',
  bg: '#F1F3F6', grid: '#D5DCE6', ink: '#0E1726', muted: '#7A8699',
  card: '#FFFFFF', cardBorder: '#E7ECF3', shadow: '0 8px 22px -12px rgba(20,40,90,.22)',
};

/* ============================ ICONS ============================ */
const I = {
  flag:'<svg viewBox="0 0 24 24" fill="none"><path d="M4 22V4M4 4h13l-2 4 2 4H4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  contacts:'<svg viewBox="0 0 24 24" fill="none"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M12 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0M22 21v-2a4 4 0 0 0-3-3.87M16 3.13A4 4 0 0 1 16 11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  connect:'<svg viewBox="0 0 24 24" fill="none"><circle cx="6" cy="6" r="2.4" stroke="currentColor" stroke-width="2"/><circle cx="18" cy="18" r="2.4" stroke="currentColor" stroke-width="2"/><circle cx="18" cy="6" r="2.4" stroke="currentColor" stroke-width="2"/><path d="M8.2 7.6 15.8 16.4M16 7.5 8 16.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
  clock:'<svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/><path d="M12 7v5l3 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  send:'<svg viewBox="0 0 24 24" fill="none"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  delay:'<svg viewBox="0 0 24 24" fill="none"><path d="M6 2h12M6 22h12M7 2c0 5 4 6 5 8 1-2 5-3 5-8M7 22c0-5 4-6 5-8 1 2 5 3 5 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  like:'<svg viewBox="0 0 24 24" fill="none"><path d="M7 10v11M2 13v6a2 2 0 0 0 2 2h13.4a2 2 0 0 0 2-1.6l1.3-7A2 2 0 0 0 18 11h-5l1-4.5A2.5 2.5 0 0 0 11.5 4L7 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  voice:'<svg viewBox="0 0 24 24" fill="none"><path d="M3 9v6h4l5 5V4L7 9H3z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M16 8.5a4 4 0 0 1 0 7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
  end:'<svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="5" fill="currentColor"/></svg>',
  plus:'<svg viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>',
  chev:'<svg viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
};

/* ============================ NODE BUILDERS ============================ */
const C = 400;                                  // single centered lane (world units)
const ic = (icon,color) => `<div class="hdy-ic" style="background:${color}1f;color:${color}">${I[icon]}</div>`;

/* campaign-template step type -> animation card presentation (icon + brand color + title) */
const STEP_PRESENTATION = {
  send_connection:     { icon:'connect', color:'#FE8888', title:'Send Connection Request' },
  wait_for_connection: { icon:'clock',   color:'#FE8888', title:'Wait for Acceptance' },
  send_message:        { icon:'send',    color:'#DF99FF', title:'Send Message' },
  delay:               { icon:'delay',   color:'#2783EC', title:'Delay' },
  send_voice_message:  { icon:'voice',   color:'#4DDBBA', title:'Send Voice Message' },
  like_post:           { icon:'like',    color:'#FAA666', title:'Like Latest Post' },
};

const PREVIEW_MAX = 46;
const previewOf = (message) => {
  const text = (message?.value || '').replace(/\s+/g, ' ').trim();
  return text.length > PREVIEW_MAX ? `${text.slice(0, PREVIEW_MAX)}…` : text;
};
const escapeHtml = (text) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const startPill = `<div class="hdy-pill"><span style="color:#8A95A6">${I.flag}</span> Automation Start</div>`;
const flowCard = (icon, color, title, sub) =>
  `<div class="hdy-card"><div class="hdy-main"><div class="hdy-chead">${ic(icon,color)}<div class="hdy-ctitle">${title}</div></div>`+
  `${sub ? `<div class="hdy-sub">${escapeHtml(sub)}</div>` : ''}</div></div>`;

/* build a linear node list straight from the campaign template steps + messages */
const buildItems = (steps = [], messages = []) => {
  const items = [{ lane:C, ax:'-50%', html: startPill }];
  let messageIndex = 0;
  steps.forEach((step) => {
    const pres = STEP_PRESENTATION[step.type] || { icon:'send', color:'#7C5CFF', title: step.type };
    let sub = '';
    if (step.type === 'send_message') { sub = previewOf(messages[messageIndex]); messageIndex += 1; }
    else if (step.type === 'delay') { sub = `${step.delayValue || ''} ${step.delayUnit || ''}`.trim(); }
    items.push({ lane:C, ax:'-104px', html: flowCard(pres.icon, pres.color, pres.title) });
  });
  items.push({ lane:C, ax:'-104px', end:true, html: flowCard('end', '#16A34A', 'End Automation', '') });
  return items;
};

/* ============================ SCOPED STYLES ============================ */
const CSS = `
.hdy-scope{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Inter,Roboto,Helvetica,Arial,sans-serif;}
.hdy-scope *{box-sizing:border-box;margin:0;padding:0;}
.hdy-root{position:relative;width:100%;height:100%;min-height:220px;overflow:hidden;}
.hdy-canvas{position:absolute;inset:0;background-color:var(--bg);
  background-image:radial-gradient(var(--grid) 1.5px,transparent 1.5px);background-size:22px 22px;}
.hdy-status{position:absolute;top:14px;left:50%;transform:translateX(-50%);z-index:20;display:flex;align-items:center;gap:8px;
  font-size:13px;font-weight:600;color:var(--brand);background:var(--brand-soft);padding:7px 13px;border-radius:999px;
  box-shadow:0 4px 14px -6px rgba(51,102,255,.4);transition:all .4s ease;}
.hdy-status .hdy-dot{width:7px;height:7px;border-radius:50%;background:var(--brand);}
.hdy-status.hdy-thinking .hdy-dot{animation:hdy-pulse 1s infinite ease-in-out;}
.hdy-status.hdy-hide{opacity:0;transform:translateX(-50%) translateY(-8px);}
@keyframes hdy-pulse{0%,100%{opacity:.25;transform:scale(.7)}50%{opacity:1;transform:scale(1.2)}}
.hdy-stage{position:absolute;inset:0;overflow:hidden;z-index:3;}
.hdy-world{position:absolute;top:0;left:0;width:800px;transform-origin:0 0;transition:transform .5s cubic-bezier(.4,.1,.2,1);}
.hdy-wires{position:absolute;top:0;left:0;width:800px;overflow:visible;z-index:1;}
.hdy-wire{fill:none;stroke:#C3D0E6;stroke-width:2.4;stroke-linecap:round;stroke-linejoin:round;
  stroke-dasharray:var(--len);stroke-dashoffset:var(--len);}
.hdy-wire.hdy-draw{animation:hdy-draw calc(.4s / var(--speed)) ease forwards;}
@keyframes hdy-draw{to{stroke-dashoffset:0;}}
.hdy-plus{position:absolute;width:26px;height:26px;border-radius:8px;background:var(--brand);color:#fff;display:grid;place-items:center;
  z-index:4;transform:translate(-50%,-50%) scale(0);box-shadow:0 5px 12px -3px rgba(51,102,255,.6);}
.hdy-plus svg{width:14px;height:14px;}
.hdy-plus.hdy-show{animation:hdy-plusIn calc(.35s / var(--speed)) cubic-bezier(.34,1.56,.64,1) forwards;}
@keyframes hdy-plusIn{to{transform:translate(-50%,-50%) scale(1);}}
.hdy-node{position:absolute;opacity:0;z-index:5;will-change:transform,opacity;transform:translate(var(--ax,-50%),0) scale(.8);}
.hdy-node.hdy-pop{animation:hdy-pop calc(.42s / var(--speed)) cubic-bezier(.34,1.56,.64,1) forwards;}
@keyframes hdy-pop{0%{opacity:0;transform:translate(var(--ax,-50%),12px) scale(.8);}60%{opacity:1;}
  100%{opacity:1;transform:translate(var(--ax,-50%),0) scale(1);}}
.hdy-node.hdy-touch .hdy-card,.hdy-node.hdy-touch .hdy-pill{box-shadow:var(--shadow),0 0 0 3px rgba(51,102,255,.16),0 0 26px 1px rgba(51,102,255,.22);}
.hdy-pill{display:flex;align-items:center;gap:8px;background:#fff;border:1px solid var(--card-border);padding:8px 16px;
  border-radius:999px;box-shadow:var(--shadow);font-size:13px;font-weight:600;color:var(--ink);white-space:nowrap;transition:box-shadow .4s ease;}
.hdy-pill svg{width:15px;height:15px;}
.hdy-pill.hdy-yes{color:var(--green);background:#E8F7EE;border-color:#BFE9CF;}
.hdy-pill.hdy-no{color:#E11D48;background:#FDE8EC;border-color:#F6C2CF;}
.hdy-pill .hdy-beacon{width:8px;height:8px;border-radius:50%;background:#4ADE80;box-shadow:0 0 0 0 rgba(74,222,128,.6);animation:hdy-beacon 1.6s infinite;}
@keyframes hdy-beacon{0%{box-shadow:0 0 0 0 rgba(74,222,128,.5)}70%{box-shadow:0 0 0 8px rgba(74,222,128,0)}100%{box-shadow:0 0 0 0 rgba(74,222,128,0)}}
.hdy-card{display:flex;background:var(--card);border:1px solid var(--card-border);border-radius:13px;box-shadow:var(--shadow);overflow:hidden;transition:box-shadow .4s ease;}
.hdy-main{padding:12px 14px;width:208px;}
.hdy-chead{display:flex;align-items:center;gap:10px;margin-bottom:2px;}
.hdy-ic{width:26px;height:26px;border-radius:8px;display:grid;place-items:center;flex:none;}
.hdy-ic svg{width:16px;height:16px;}
.hdy-ctitle{font-size:13.5px;font-weight:700;letter-spacing:-.1px;color:var(--ink);}
.hdy-sub{margin-top:9px;font-size:12px;line-height:1.45;color:var(--muted);background:#F6F8FB;border:1px solid #EDF1F6;border-radius:8px;padding:7px 9px;}
.hdy-drop{margin-top:10px;display:flex;align-items:center;justify-content:space-between;border:1px solid #E4E9F1;border-radius:8px;
  padding:8px 11px;font-size:12.5px;color:#9AA6B6;}
.hdy-drop svg{width:14px;height:14px;color:#B6C0CE;}
.hdy-tbl{margin-top:9px;width:100%;}
.hdy-trow{display:flex;justify-content:space-between;align-items:center;padding:5px 0;font-size:12px;font-weight:600;border-bottom:1px solid #F2F5F9;}
.hdy-trow.hdy-h{color:#9AA6B6;font-size:10.5px;letter-spacing:.3px;font-weight:700;text-transform:uppercase;border-bottom:1px solid #ECF0F6;}
.hdy-trow.hdy-h .hdy-cnt{color:#9AA6B6;}
.hdy-trow:last-child{border-bottom:none;}
.hdy-trow .hdy-cnt{display:flex;align-items:center;gap:5px;font-variant-numeric:tabular-nums;}
.hdy-trow .hdy-cnt i{font-style:normal;font-size:10px;opacity:.55;}
.hdy-ok{color:var(--green);} .hdy-bad{color:#EF4444;} .hdy-prog{color:var(--brand);}
.hdy-particle{position:absolute;border-radius:50%;pointer-events:none;z-index:32;}
.hdy-glow{position:absolute;inset:0;z-index:2;opacity:0;pointer-events:none;
  background:radial-gradient(60% 50% at 50% 50%,rgba(51,102,255,.14),transparent 70%);}
.hdy-glow.hdy-show{animation:hdy-rg .7s ease forwards;}
@keyframes hdy-rg{to{opacity:1;}}
.hdy-success{position:absolute;top:16px;right:16px;z-index:30;opacity:0;pointer-events:none;transform:translateX(calc(100% + 26px));
  display:flex;flex-direction:row;align-items:center;text-align:left;gap:11px;width:262px;
  background:rgba(252,252,254,.86);-webkit-backdrop-filter:blur(16px) saturate(1.5);backdrop-filter:blur(16px) saturate(1.5);
  padding:11px 13px;border-radius:16px;border:1px solid rgba(255,255,255,.65);
  box-shadow:0 12px 30px -8px rgba(14,23,38,.22),0 2px 8px -3px rgba(14,23,38,.14);}
.hdy-success.hdy-show{animation:hdy-successIn calc(.6s / var(--speed)) cubic-bezier(.3,1.25,.5,1) forwards;}
@keyframes hdy-successIn{to{opacity:1;transform:translateX(0);}}
.hdy-ring{width:34px;height:34px;border-radius:10px;background:var(--green);display:grid;place-items:center;flex:none;box-shadow:0 3px 8px -2px rgba(22,163,74,.5);}
.hdy-ring svg{width:19px;height:19px;color:#fff;}
.hdy-success .hdy-st{font-size:13px;font-weight:700;color:var(--ink);letter-spacing:-.2px;}
.hdy-success .hdy-ss{font-size:11.5px;color:var(--muted);line-height:1.35;margin-top:1px;}
.hdy-controls{display:flex;align-items:center;gap:10px;flex-wrap:wrap;justify-content:center;margin-top:16px;background:#fff;
  padding:12px 16px;border-radius:14px;box-shadow:0 8px 24px -14px rgba(20,40,90,.3);}
.hdy-controls .hdy-grp{display:flex;gap:6px;background:#F1F4FA;padding:4px;border-radius:10px;}
.hdy-controls button{font:inherit;font-size:13px;font-weight:600;cursor:pointer;border:none;border-radius:9px;padding:9px 15px;color:var(--ink);background:transparent;transition:all .15s ease;}
.hdy-controls .hdy-grp button.hdy-active{background:#fff;color:var(--brand);box-shadow:0 2px 6px -2px rgba(20,40,90,.25);}
.hdy-controls .hdy-play{background:var(--brand-strong);color:#fff;padding:9px 20px;}
.hdy-controls .hdy-lbl{font-size:12px;color:var(--muted);font-weight:600;align-self:center;padding-left:6px;}
`;

/* ============================ COMPONENT ============================ */
export default function CampaignBuildAnimation({
  steps = [], messages = [],
  speed = 0.7, ending = 'stay', autoPlay = true, loop = false,
  showStatus = true, showGrid = true,
  successTitle = 'Congratulations!',
  successText = 'Your campaign is set up and ready to launch.',
  showControls = false, tokens = {}, onComplete, className = '', style = {},
}) {
  const rootRef = useRef(null);
  const worldRef = useRef(null);
  const wiresRef = useRef(null);
  const statusRef = useRef(null);
  const statusTextRef = useRef(null);
  const successRef = useRef(null);
  const glowRef = useRef(null);

  const speedRef = useRef(speed);
  const endingRef = useRef(ending);
  const loopRef = useRef(loop);
  const runningRef = useRef(false);
  const cancelRef = useRef(false);
  const worldHRef = useRef(2200);

  useEffect(() => { speedRef.current = speed; rootRef.current?.style.setProperty('--speed', speed); }, [speed]);
  useEffect(() => { endingRef.current = ending; }, [ending]);
  useEffect(() => { loopRef.current = loop; }, [loop]);

  /* ---------- the animation (imperative; React owns the mount) ---------- */
  async function run() {
    if (runningRef.current) return;
    const world = worldRef.current, wires = wiresRef.current, root = rootRef.current;
    if (!world || !wires || !root) return;
    runningRef.current = true; cancelRef.current = false;

    const vp = { W: root.clientWidth || 900, H: root.clientHeight || 560 };
    const Sb = Math.min(1, (vp.W - 40) / 800);            // build scale (fits narrow containers)
    const SP = () => speedRef.current;
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms / SP()));
    const dead = () => cancelRef.current || !worldRef.current;
    const setCls = (ref, cls) => { if (ref.current) ref.current.className = cls; };

    const bottom = (p) => p.top + p.h;
    const vline = (x, y1, y2) => `M ${x} ${y1} L ${x} ${y2}`;
    const mkNode = (cx, html, ax) => {
      const n = document.createElement('div'); n.className = 'hdy-node';
      n.style.setProperty('--ax', ax); n.style.left = cx + 'px'; n.innerHTML = html;
      world.appendChild(n); return n;
    };
    const camera = (focusY, S, center, f = 0.4) => {
      const tx = (vp.W - 800 * S) / 2;
      const ty = center ? (vp.H - worldHRef.current * S) / 2 : (vp.H * f - focusY * S);
      world.style.transform = `translate(${tx}px,${ty}px) scale(${S})`;
    };
    const makeWire = (d) => {
      const p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      p.setAttribute('d', d); p.setAttribute('class', 'hdy-wire'); wires.appendChild(p);
      p.style.setProperty('--len', p.getTotalLength()); return p;
    };
    const addPlus = (x, y) => {
      const b = document.createElement('div'); b.className = 'hdy-plus'; b.innerHTML = I.plus;
      b.style.left = x + 'px'; b.style.top = y + 'px'; world.appendChild(b);
      requestAnimationFrame(() => b.classList.add('hdy-show'));
    };
    const sparkle = (el) => {
      const fr = world.getBoundingClientRect(), r = el.getBoundingClientRect();
      const sx = fr.width / 800, sy = fr.height / worldHRef.current;
      const cx = (r.left - fr.left + r.width / 2) / sx, cy = (r.top - fr.top + r.height / 2) / sy;
      const colors = ['#3366FF', '#7C5CFF', '#FF9F43', '#37D67A', '#EC4899'];
      for (let i = 0; i < 9; i++) {
        const p = document.createElement('div'); p.className = 'hdy-particle';
        p.style.width = p.style.height = (5 + Math.random() * 3) + 'px';
        p.style.background = colors[i % colors.length];
        p.style.left = cx + 'px'; p.style.top = cy + 'px'; world.appendChild(p);
        const a = (Math.PI * 2 * i) / 9 + Math.random() * 0.5, d = 30 + Math.random() * 30;
        p.animate([{ transform: 'translate(-50%,-50%) scale(1)', opacity: 1 },
          { transform: `translate(${Math.cos(a) * d - 50}%,${Math.sin(a) * d - 50}%) scale(0)`, opacity: 0 }],
          { duration: 600 / SP(), easing: 'cubic-bezier(.2,.8,.3,1)' }).onfinish = () => p.remove();
      }
    };
    const pop = (el) => { el.classList.add('hdy-pop', 'hdy-touch'); sparkle(el); setTimeout(() => el.classList.remove('hdy-touch'), 520 / SP()); };

    /* ---- measure + lay out everything ---- */
    world.querySelectorAll('.hdy-node,.hdy-plus').forEach((e) => e.remove());
    wires.innerHTML = '';
    const stage = world.parentNode;            // .hdy-stage — becomes scrollable once built
    if (stage) {
      stage.style.overflowY = 'hidden';
      stage.querySelectorAll('.hdy-sizer').forEach((e) => e.remove());
    }
    const items = buildItems(steps, messages);
    const G = 54, GM = 96;
    const P = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const top = i === 0 ? 0 : bottom(P[i - 1]) + (it.end ? GM : G);
      const el = mkNode(it.lane, it.html, it.ax); el.style.top = top + 'px';
      P[i] = { el, cx: it.lane, top, h: el.offsetHeight, cy: top + el.offsetHeight / 2 };
    }
    worldHRef.current = bottom(P[P.length - 1]) + 90;
    world.style.height = worldHRef.current + 'px';
    wires.style.height = worldHRef.current + 'px';
    wires.setAttribute('viewBox', `0 0 800 ${worldHRef.current}`);

    /* ---- connectors: a vertical wire + plus between each consecutive node ---- */
    const CN = [];
    const add = (d, plus, at) => CN.push({ d, plus, at });
    const mid = (a, b) => (bottom(a) + b.top) / 2;
    for (let i = 0; i < items.length - 1; i++) {
      add(vline(C, bottom(P[i]), P[i + 1].top), [C, mid(P[i], P[i + 1])], i + 1);
    }

    /* ---- reset chrome ---- */
    setCls(statusRef, 'hdy-status hdy-thinking');
    if (statusTextRef.current) statusTextRef.current.textContent = 'AI is building your campaign…';
    setCls(successRef, 'hdy-success');
    setCls(glowRef, 'hdy-glow');
    world.style.transition = 'none'; camera(P[0].cy, Sb); await sleep(40);
    if (dead()) { runningRef.current = false; return; }
    world.style.transition = '';

    /* ---- build ---- */
    for (let i = 0; i < items.length; i++) {
      if (dead()) { runningRef.current = false; return; }
      const p = P[i];
      camera(p.cy, Sb);
      const cons = CN.filter((c) => c.at === i);
      cons.forEach((c) => { if (c.d) { const path = makeWire(c.d); requestAnimationFrame(() => path.classList.add('hdy-draw')); } });
      setTimeout(() => cons.forEach((c) => { if (c.plus) addPlus(c.plus[0], c.plus[1]); }), 140 / SP());
      await sleep(cons.length ? 130 : 90);
      pop(p.el); await sleep(170);
    }

    await sleep(100);
    if (dead()) { runningRef.current = false; return; }
    if (endingRef.current === 'reveal') {
      const S = Math.min(Sb, (vp.H - 60) / worldHRef.current, (vp.W - 60) / 800);
      camera(worldHRef.current / 2, S, true);
      await sleep(820);
    } else {
      // Settle showing the top of the finished template, then let the user scroll
      // down through the rest of the sequence.
      const tx = (vp.W - 800 * Sb) / 2;
      world.style.transform = `translate(${tx}px, 16px) scale(${Sb})`;
      if (stage) {
        stage.style.overflowY = 'auto';
        const sizer = document.createElement('div');
        sizer.className = 'hdy-sizer';
        sizer.style.height = `${worldHRef.current * Sb + 32}px`;
        stage.appendChild(sizer);
      }
      await sleep(560);
    }
    if (dead()) { runningRef.current = false; return; }
    setCls(statusRef, 'hdy-status hdy-thinking hdy-hide');   // fade the building pill out
    await sleep(140);
    if (dead()) { runningRef.current = false; return; }
    setCls(successRef, 'hdy-success hdy-show');              // congrats toast floats up from the bottom
    await sleep(560);

    runningRef.current = false;
    if (!dead() && typeof onComplete === 'function') onComplete();
    if (!dead() && loopRef.current) setTimeout(() => run(), 1400 / SP());
  }

  useEffect(() => {
    cancelRef.current = false;
    let t;
    if (autoPlay) t = setTimeout(() => run(), 350);
    return () => { cancelRef.current = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = { ...DEFAULT_TOKENS, ...tokens };
  const cssVars = {
    '--brand': t.brand, '--brand-strong': t.brandStrong, '--brand-soft': t.brandSoft,
    '--violet': t.violet, '--pink': t.pink, '--red': t.red, '--amber': t.amber,
    '--teal': t.teal, '--blue2': t.blue2, '--green': t.green,
    '--bg': t.bg, '--grid': t.grid, '--ink': t.ink, '--muted': t.muted,
    '--card': t.card, '--card-border': t.cardBorder, '--shadow': t.shadow,
    '--speed': speedRef.current,
  };

  return (
    <div className="hdy-scope" style={cssVars}>
      <style>{CSS}</style>
      <div ref={rootRef} className={`hdy-root ${className}`} style={style}>
        {showGrid && <div className="hdy-canvas" />}
        <div className="hdy-glow" ref={glowRef} />
        {showStatus && (
          <div className="hdy-status hdy-thinking" ref={statusRef}>
            <span className="hdy-dot" /><span ref={statusTextRef}>AI is building your campaign…</span>
          </div>
        )}
        <div className="hdy-stage">
          <div className="hdy-world" ref={worldRef}>
            <svg className="hdy-wires" ref={wiresRef} preserveAspectRatio="none" />
          </div>
        </div>
        {/*<div className="hdy-success" ref={successRef}>*/}
        {/*  <div className="hdy-ring">*/}
        {/*    <svg viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg>*/}
        {/*  </div>*/}
        {/*  <div>*/}
        {/*    <div className="hdy-st">{successTitle}</div>*/}
        {/*    <div className="hdy-ss">{successText}</div>*/}
        {/*  </div>*/}
        {/*</div>*/}
      </div>

      {showControls && (
        <div className="hdy-controls">
          <button className="hdy-play" onClick={() => run()}>▶ Replay</button>
        </div>
      )}
    </div>
  );
}
